<script>
import baseMixin from '@/mixins/baseMixin'
import ownerMixin from '@/mixins/ownerMixin'
import dayjs from '@/services/date'
import { mapGetters, mapActions } from 'vuex'

export default {
  name: 'CancelBookingDialog',
  mixins: [baseMixin, ownerMixin],
  props: {
    booking: {
      type: Object,
      required: true,
    },
    show: {
      type: Boolean,
      required: true,
    },
  },
  data: () => {
    return {
      payload: {},
    }
  },
  computed: {
    ...mapGetters({
      isLoadingCancelBooking: 'bookings/isLoadingCancelBooking',
    }),
    isBookingInPast() {
      const localStartTime = dayjs(this.booking.startDateTimeLocal)
      const localNow = dayjs()
      return localNow.isAfter(localStartTime)
    },
    hasAlreadyBeenAssignedToACandidate() {
      return this.booking.candidate && this.booking.candidate.id > 1
    },
    canCancelBooking() {
      return !this.isBookingInPast && !this.hasAlreadyBeenAssignedToACandidate
    },
    formattedStartDate() {
      return this.$i18n.d(
        dayjs(this.booking.startDateTimeLocal),
        'dateFormatted'
      )
    },
    compTitle() {
      return this.canCancelBooking
        ? this.$t('booking.cancelBookingDialogTitle', {
            date: this.formattedStartDate,
          })
        : this.$t('booking.unableToCancelBookingDialogTitle')
    },
    compInputLabel() {
      return this.isMobileViewPort
        ? this.$t('booking.cancelBookingInputMobileLabel')
        : this.$t('booking.cancelBookingInputLabel')
    },
    isSubmitButtonDisabled() {
      return !this.canCancelBooking || !this.payload?.cancelReason
    },
    unableToCancelDescription() {
      return this.$t('booking.unableToCancelBookingDescription', {
        companyName: this.$t('app.companyName'),
      })
    },
    compConsultantLabel() {
      return this.$t('consultant.consultantLabel', {
        companyName: this.$t('app.companyName'),
      })
    },
    compReasonForBeingUnableToCancel() {
      if (this.canCancelBooking) return ''

      if (this.isBookingInPast)
        return this.$t('booking.cancelBookingUnableReasonInPast')

      if (this.hasAlreadyBeenAssignedToACandidate)
        return this.$t('booking.cancelBookingUnableReasonAlreadyAssigned')

      return ''
    },
  },
  async mounted() {
    this.payload = {}
    await this.$nextTick()
    this.payload = {
      bookingId: this.booking.id,
      date: this.formattedStartDate,
      cancelReason: '',
    }
  },
  methods: {
    ...mapActions({
      cancelBooking: 'bookings/cancelBooking',
    }),
    async submit() {
      if (this.isSubmitButtonDisabled) return

      const result = await this.cancelBooking(this.payload)

      if (!result.isSuccess) return

      this.$emit('closeModalAndRefresh')
    },
  },
}
</script>

<template>
  <v-dialog
    v-model="show"
    max-width="600"
    :persistent="isLoadingCancelBooking"
    @input="$emit('closeModal')"
  >
    <v-card id="cancel-booking-dialog">
      <v-card-title> {{ compTitle }} </v-card-title>
      <v-card-text v-if="canCancelBooking">
        <v-textarea
          id="cancel-booking-form__reason"
          ref="cancelBookingFormReason"
          v-model="payload.cancelReason"
          name="cancelReason"
          :label="compInputLabel"
          required
        ></v-textarea>
      </v-card-text>
      <v-card-text
        v-else
        class="cancel-booking-dialog__unable-to-cancel-section"
      >
        <p>
          {{ unableToCancelDescription }}
        </p>

        <div v-if="compReasonForBeingUnableToCancel">
          <span class="caption">
            {{ $t('booking.cancelBookingUnableReasonLabel') }}
          </span>
          <p>
            {{ compReasonForBeingUnableToCancel }}
          </p>
        </div>

        <div v-if="owner">
          <span class="caption">
            {{ compConsultantLabel }}
          </span>
          <p>
            {{ ownerName }}
            <a :title="`tel:${ownerPhone}`" :href="`tel:${ownerPhone}`">
              {{ ownerPhone }}
            </a>
          </p>
        </div>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn
          text
          :disabled="isLoadingCancelBooking"
          @click="$emit('closeModal')"
        >
          {{ $t('general.close') }}
        </v-btn>
        <v-btn
          id="cancel-booking-dialog__submit-btn"
          :disabled="isSubmitButtonDisabled"
          color="red"
          text
          :loading="isLoadingCancelBooking"
          @click="submit"
        >
          {{ $t('booking.cancelBookingBtn') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
